/**
 * Cookie Consent Manager
 * Handles user consent for analytics and caching cookies
 * Compliant with GDPR, CCPA, and other privacy regulations
 */

const CONSENT_KEY = 'prepxai_cookie_consent';

export const hasPerformanceCacheConsent = () => {
  try {
    const stored = localStorage.getItem(CONSENT_KEY);
    if (!stored) return false;
    return JSON.parse(stored)?.caching === true;
  } catch (error) {
    return false;
  }
};

class CookieConsentManager {
  constructor() {
    this.consentKey = CONSENT_KEY;
    this.preferenceRevision = 0;
    this.preferences = this.loadPreferences();
  }

  clearPerformanceCache() {
    return import('./cache-manager')
      .then(({ getCacheManager }) => getCacheManager().clearPerformanceCache())
      .catch((error) => {
        console.error('Performance cache cleanup failed:', error);
        throw error;
      });
  }

  /**
   * Load consent preferences from localStorage
   */
  loadPreferences() {
    const stored = localStorage.getItem(this.consentKey);
    if (stored) {
      try {
        return JSON.parse(stored);
      } catch (error) {
        localStorage.removeItem(this.consentKey);
      }
    }

    return {
      necessary: true, // Always required
      analytics: false,
      caching: false, // For performance caching
      marketing: false,
      accepted: false,
      timestamp: null,
    };
  }

  /**
   * Save consent preferences
   */
  async savePreferences(preferences) {
    const revision = ++this.preferenceRevision;
    const updated = {
      ...this.preferences,
      ...preferences,
      timestamp: Date.now(),
      accepted: true,
    };

    const enablingCaching = updated.caching && !this.preferences.caching;
    if (enablingCaching) {
      // Keep the cache gate closed until stale data from older sessions is gone.
      const pending = { ...updated, caching: false };
      localStorage.setItem(this.consentKey, JSON.stringify(pending));
      this.preferences = pending;
      await this.clearPerformanceCache();
      if (revision !== this.preferenceRevision) {
        return this.preferences;
      }
    }

    localStorage.setItem(this.consentKey, JSON.stringify(updated));
    this.preferences = updated;
    if (!updated.caching) {
      await this.clearPerformanceCache();
    }
    return updated;
  }

  /**
   * Check if specific consent is given
   */
  hasConsent(type) {
    return this.preferences[type] === true;
  }

  /**
   * Accept all cookies
   */
  acceptAll() {
    return this.savePreferences({
      necessary: true,
      analytics: true,
      caching: true,
      marketing: true,
    });
  }

  /**
   * Accept only necessary
   */
  acceptNecessary() {
    return this.savePreferences({
      necessary: true,
      analytics: false,
      caching: false,
      marketing: false,
    });
  }

  /**
   * Check if consent banner was shown
   */
  isConsentGiven() {
    return this.preferences.accepted === true;
  }

  /**
   * Get consent status
   */
  getPreferences() {
    return {
      ...this.preferences,
      isGiven: this.isConsentGiven(),
    };
  }

  /**
   * Reset consent (useful for privacy/settings pages)
   */
  async resetConsent() {
    this.preferenceRevision += 1;
    localStorage.removeItem(this.consentKey);
    this.preferences = this.loadPreferences();
    await this.clearPerformanceCache();
  }
}

// Singleton instance
let consentManager = null;

export const getCookieConsentManager = () => {
  if (!consentManager) {
    consentManager = new CookieConsentManager();
  }
  return consentManager;
};

export default CookieConsentManager;
